import React, { useState, useRef, lazy, Suspense } from 'react';
import styled from '@emotion/styled';
import MarketplaceForm from './components/MarketplaceForm';
import NavigationBar from './components/NavigationBar';
import PreviewHeader from './components/PreviewHeader';
import FAQ from './components/FAQ';
import SecurityInfo from './components/SecurityInfo';
import './print-styles.css'; // Import print styles

// Lazy load the preview component since it's not needed immediately
const MarketplacePreview = lazy(() => import('./components/MarketplacePreview'));

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Main = styled.main`
  flex: 1;
  padding: 40px 20px;
  background-color: #F4F5F7;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(#DFE1E6 2px, transparent 2px);
    background-size: 24px 24px;
    background-position: 12px -12px;
    opacity: 0.5;
  }
`;

const Content = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
  position: relative;
  z-index: 1;
`;

const FormPreviewContainer = styled.div`
  display: grid;
  grid-template-columns: 500px 1fr;
  gap: 40px;
  align-items: start;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Footer = styled.footer`
  background-color: white;
  padding: 24px 20px;
  border-top: 1px solid #DFE1E6;
`;

const FooterContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  
  .copyright {
    font-size: 14px;
    font-weight: 500;
    color: #172B4D;
    margin-top: 16px;
  }
  
  .disclaimer {
    font-size: 13px;
    line-height: 1.6;
    color: #6B778C;
  }
`;

// Memoize static components
const MemoizedNavigationBar = React.memo(NavigationBar);
const MemoizedPreviewHeader = React.memo(PreviewHeader);

function App() {
  const initialFormData = {
    appName: '',
    appTagline: '',
    appLogo: null,
    appBanner: null,
    appYouTubeUrl: '',
    companyName: '',
    worksWithProducts: {
      jiraCloud: false,
      jiraDataCenter: false,
      confluenceCloud: false,
      confluenceDataCenter: false
    },
    highlightTitle1: '',
    highlightSummary1: '',
    highlightScreenshot1: null,
    highlightTitle2: '',
    highlightSummary2: '',
    highlightScreenshot2: null,
    highlightTitle3: '',
    highlightSummary3: '',
    highlightScreenshot3: null,
    supportingMedia1: null,
    supportingMedia2: null,
    supportingMedia3: null,
    supportingMedia4: null,
    supportingMedia5: null,
    moreDetails: '',
    videoUrl: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const previewRef = useRef(null);

  const handleFormChange = (data) => {
    setFormData(data);
  };

  return (
    <AppContainer>
      <MemoizedNavigationBar />
      <Main>
        <Content>
          <MemoizedPreviewHeader />
          <SecurityInfo />
          <FormPreviewContainer>
            <MarketplaceForm 
              formData={formData}
              onChange={handleFormChange}
            />
            <PreviewColumn>
              <Suspense fallback={
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '200px',
                  backgroundColor: '#FAFBFC',
                  borderRadius: '3px'
                }}>
                  <div style={{ 
                    width: '30px', 
                    height: '30px', 
                    border: '2px solid #DFE1E6',
                    borderRadius: '50%',
                    borderTopColor: '#0052CC',
                    animation: 'spin 0.6s linear infinite'
                  }} />
                </div>
              }>
                <MarketplacePreview ref={previewRef} formData={formData} />
              </Suspense>
              <FAQ />
            </PreviewColumn>
          </FormPreviewContainer>
        </Content>
      </Main>
      <Footer>
        <FooterContent>
          <div className="copyright">© {new Date().getFullYear()} Marketplace Listing Preview</div>
          <div className="disclaimer">
            This tool is not affiliated with, endorsed by, or connected to Atlassian. 
            It is an independent utility for previewing your marketplace listings. Please keep in mind that this is not a real listing and is for previewing purposes only.
          </div>
        </FooterContent>
      </Footer>
    </AppContainer>
  );
}

export default App; 